package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetSkeleton
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.text.resolveOrNull
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * Where an invite link lands. SDS §27.8: "Support join via invite link."
 *
 * The SDS designs no screen for this either, so the layout is a DECISION and it is deliberately the
 * same shape as `GroupScreen`'s identity block -- avatar, name, "Public group · 12 members",
 * description -- because a person following a link is being asked to recognise a community, and
 * recognising it should not depend on which screen they happen to be looking at. What is NOT
 * reused is `GroupScreen` itself: this viewer is not a member, there is no chat to open, no member
 * list to browse and no conversation id to address, and a screen that showed those rows greyed out
 * would be advertising a group's insides to somebody standing outside it.
 *
 *
 * THE STATES, ALL SIX
 *
 *  * FIRST FRAME -- a skeleton at the sizes the identity block really uses (SDS §2.1), not a
 *    spinner. A link opens this screen cold, so the reflow a spinner guarantees would happen on
 *    every single arrival rather than only on a slow connection.
 *  * LOADED -- the group, one sentence saying what the button will do, and the button.
 *  * TERMINAL -- dead link, banned, full, unverified email. One sentence, no action, no Retry:
 *    every one of those is settled, and "Try again" would be an offer that cannot be honoured.
 *  * WAITING -- a private group's request is on file. The button is gone, not disabled: there is
 *    nothing left to press, and SDS §25.1's disabled state is for something momentarily
 *    unavailable.
 *  * ERROR -- offline, timeout, 5xx, rate limit. A banner over whatever is on screen, with Retry.
 *  * JOINED -- a one-shot signal the caller consumes and navigates on.
 *
 * The BANNED case reaches this screen only after the button is pressed. That is the contract's
 * design, not a gap here: `GroupInvitePreview.action` has no banned value, because a preview that
 * refused would tell whoever holds the link that this person is banned. See `JoinGroupViewModel`.
 */
@Composable
fun JoinGroupScreen(
    state: JoinGroupUiState,
    /**
     * Back out of the invite. On a cold start from a link this is the only way out, so a caller
     * that pushed this route onto an empty stack has to send it somewhere real (the Chats tab)
     * rather than popping into nothing.
     */
    onBack: () -> Unit,
    /** Join, request access, or open the group -- whichever the single button currently is. */
    onAct: () -> Unit,
    /**
     * Consumed one-shot when a join lands: navigate to the group and call
     * [JoinGroupViewModel.consumeJoined] first, or the next recomposition navigates again.
     */
    onJoined: (GroupId) -> Unit,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
) {
    val colors = SwetgramThemeTokens.colors
    val invite = state.invite

    // Keyed on the id so it fires once per join rather than on every recomposition. The caller
    // clears the signal before navigating; see the parameter's note.
    LaunchedEffect(state.joinedGroupId) {
        state.joinedGroupId?.let(onJoined)
    }

    Column(modifier.fillMaxSize()) {
        // TODO(localization): every user-facing string in this file moves into resources (EN + RU).
        //
        // "Invite" rather than the group's name: the name is the largest thing a few dp below, and
        // at the moment this screen opens the user does not yet know whether the link is real.
        SwetTopAppBar(title = "Invite", onBack = onBack)

        // Only for the retryable failures. A terminal answer takes over the body instead -- see
        // the empty state below -- because a banner with Retry beside "you are banned" would be
        // offering something that cannot happen.
        if (state.error != null) {
            SwetErrorBanner(message = state.error, onRetry = onRetry, onDismiss = onDismissError)
        }

        when {
            state.loading -> JoinSkeleton()

            // Settled, and it reads as a full-screen answer rather than as an error over content,
            // because there is no content underneath it that would be worth keeping.
            state.blocked != null -> SwetEmptyState(
                title = joinBlockTitle(state.blocked),
                body = joinBlockBody(state.blocked),
            )

            invite == null -> SwetEmptyState(
                title = "Could not open this link.",
                body = "Check your connection and try again.",
                actionText = onRetry?.let { "Try again" },
                onAction = onRetry,
            )

            else -> Column(
                modifier = Modifier
                    .fillMaxSize()
                    .navigationBarsPadding()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = Space.screenMargin),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Spacer(Modifier.height(Space.four))

                SwetAvatar(
                    displayName = invite.name,
                    size = Sizes.avatarProfile,
                    surfaceColor = colors.background,
                    avatarUrl = invite.avatarUrl,
                    // A group is never online, and this one is not even joined yet.
                    isOnline = false,
                    onlineContentDescription = null,
                )

                Spacer(Modifier.height(Space.two))

                Text(
                    text = invite.name,
                    style = MaterialTheme.typography.headlineSmall,
                    color = colors.textPrimary,
                    textAlign = TextAlign.Center,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(Modifier.height(Space.half))

                Text(
                    // The same line `GroupScreen` draws, minus the handle: `GroupInvitePreview`
                    // carries no username and no public URL, and inventing one from the group's
                    // name would be inventing an address.
                    text = groupSubtitle(
                        visibility = invite.visibility,
                        username = null,
                        memberCount = invite.memberCount,
                    ).resolve(),
                    style = MaterialTheme.typography.bodyMedium,
                    color = colors.textSecondary,
                    textAlign = TextAlign.Center,
                )

                if (!invite.description.isNullOrBlank()) {
                    Spacer(Modifier.height(Space.two))
                    Text(
                        text = invite.description,
                        style = MaterialTheme.typography.bodyMedium,
                        color = colors.textPrimary,
                        textAlign = TextAlign.Center,
                    )
                }

                Spacer(Modifier.height(Space.four))

                // What pressing it does, before it is pressed -- including the two cases where
                // there is nothing to press, which is why this is never null.
                Text(
                    text = joinActionExplanation(state.action),
                    style = MaterialTheme.typography.bodySmall,
                    color = colors.textSecondary,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth(),
                )

                Spacer(Modifier.height(Space.two))

                // Absent, never disabled, when there is nothing to do: a request already waiting,
                // or an action this build could not read.
                joinActionLabel(state.action)?.let { label ->
                    SwetPrimaryButton(
                        text = label,
                        onClick = onAct,
                        loading = state.busy,
                    )
                }

                Spacer(Modifier.height(Space.six))
            }
        }
    }
}

/**
 * The first frame. The same measurements as `GroupScreen`'s skeleton, because the screen it stands
 * in for is the same identity block.
 */
@Composable
private fun JoinSkeleton() {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = Space.screenMargin),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Spacer(Modifier.height(Space.four))

        SwetSkeleton(Modifier.size(Sizes.avatarProfile), shape = CircleShape)

        Spacer(Modifier.height(Space.two))
        // Deliberately unequal, as every other skeleton here is: two bars of the same width read
        // as a rendering fault rather than as a placeholder.
        SwetSkeleton(Modifier.fillMaxWidth(0.5f).height(Space.three))
        Spacer(Modifier.height(Space.one))
        SwetSkeleton(Modifier.fillMaxWidth(0.34f).height(Space.two))

        Spacer(Modifier.height(Space.four))
        SwetSkeleton(
            Modifier.fillMaxWidth().height(TouchTarget.buttonHeight),
            shape = RoundedCornerShape(Radius.md),
        )
    }
}
